import React, { useEffect, useMemo } from 'react'

import { UnrecoverableErrorStore } from './store'

import { ExitButton } from '@/components/shared/UnrecoverableError/components/ExitButton'
import { InstructionsList } from '@/components/shared/UnrecoverableError/components/InstructionsList'
import { getCmsQuery } from '@/components/shared/UnrecoverableError/helpers/get-cms-query'
import { parser } from '@/components/shared/UnrecoverableError/parser'
import {
  Container,
  Content,
  Footer,
  Footnote,
  Header,
} from '@/components/shared/UnrecoverableError/UnrecoverableError.styled'
import { UnrecoverableErrorPageAnalytics } from '@/components/shared/UnrecoverableError/UnrecoverableErrorPage.analytics'
import { VeneerIcon } from '@/components/shared/VeneerIcon/VeneerIcon'
import { ENTRIES } from '@/constants/content-stack'
import { SPLUNK_RUM_FIELDS } from '@/constants/splunk'
import { ErrorCode } from '@/errors/classes/ErrorCode'
import { useContentStack } from '@/services/content-stack'
import { useSplunkRum } from '@/services/splunk-rum'
import { useDependencyManagerContext } from '@foreign-clientos/dependency-manager'

type UnrecoverableErrorPageProps = Readonly<{
  error: unknown
}>

export function UnrecoverableErrorPage({
  error: unknownError,
}: UnrecoverableErrorPageProps) {
  const { translate } = useDependencyManagerContext()
  const { analyticsConfig } = UnrecoverableErrorStore.use

  const error = useMemo(
    () =>
      unknownError instanceof ErrorCode
        ? unknownError
        : new ErrorCode(undefined, unknownError),
    [unknownError],
  )

  const { publishRumEvent } = useSplunkRum()
  const { entry } = useContentStack({
    entryParams: ENTRIES.UNRECOVERABLE_ERROR,
    query: getCmsQuery(error),
    parser,
    onError: (_, setFallbackEntry) => {
      setFallbackEntry(
        translate('unrecoverableError', {
          returnObjects: true,
          lng: 'en_US',
        }),
      )
    },
  })

  useEffect(() => {
    OobeLogger.error('An unrecoverable error occurred', { error })
    UnrecoverableErrorPageAnalytics.init({
      error,
      ...analyticsConfig,
    })
    UnrecoverableErrorPageAnalytics.fireScreenDisplayed()
    publishRumEvent({ [SPLUNK_RUM_FIELDS.WORKFLOW]: error.errorCode })
  }, [])

  if (!entry) return <></>

  return (
    <Container data-testid="unrecoverable-error">
      <Content>
        <Header>
          <VeneerIcon
            icon="MinusCircle"
            size={65}
            color="red6"
          />
          <p>{entry.header}</p>
        </Header>
        <InstructionsList />
        <Footnote>
          <span>{entry.errorLabel} </span>
          <span data-testid="error-code">{error.errorCode}</span>
          <div>{entry.errorNote}</div>
        </Footnote>
      </Content>
      <Footer>
        <ExitButton error={error} />
      </Footer>
    </Container>
  )
}
